/**
 * The visitor's own journey state, in one localStorage key.
 *
 * Two things live here and both are load-bearing:
 *
 *  1. The trail — the last N page ids visited, so an arrival can report the
 *     route that produced it. Page IDS only, which is what keeps the stored
 *     payload tiny and keeps the record non-identifying.
 *  2. Per-visitor de-duplication flags. Every journey metric is counted once
 *     per visitor per destination PER 30 DAYS, because otherwise "saw the
 *     button minus arrived" is meaningless: one visitor passing three buttons
 *     on four pages would produce twelve impressions. De-duplication happens
 *     here, in the browser, so no identifier ever has to be sent.
 *
 *     The flags used to be a bare `1` that never expired, so a browser was
 *     counted once for life: a prospect who reached /book-a-demo in September
 *     and came back to book in November never reached it again in any report.
 *     Every flag is now the epoch ms it was claimed at, and a claim older than
 *     the window is free again. A report range up to 30 days therefore still
 *     counts each visitor once; a 90-day range can count a returning visitor
 *     up to three times, which is the honest reading of "came back".
 *
 * Written only under statistics consent — the caller gates that.
 */

import type { CtaRef } from '../types.ts';

interface JourneyState {
	/** Trail: [postId, epochMs] pairs, oldest first. */
	t: Array<[number, number]>;
	/** destKey → the button that was clicked toward it. */
	c: Record<string, CtaRef>;
	/** destKey → epoch ms of that click, so a stale click never names an arrival. */
	ct: Record<string, number>;
	/** destKey → destination-level button impression counted at (epoch ms). */
	r: Record<string, number>;
	/** `${destKey}:${ctaKey}` → that button's impression counted at. */
	rc: Record<string, number>;
	/** `${destKey}:${ctaKey}` → that button's click counted at. */
	cc: Record<string, number>;
	/** destKey → arrival counted at. */
	a: Record<string, number>;
	/** destKey → form view counted at. */
	fv: Record<string, number>;
	/** destKey → form start counted at. */
	fs: Record<string, number>;
	/** destKey → submit counted at. */
	fd: Record<string, number>;
	/** destKey → epochMs of arrival, for the arrive-to-submit timing. */
	at: Record<string, number>;
	/** `${destKey}:${guid}:${field}:${metric}` → that field metric counted at. */
	fl: Record<string, number>;
	/**
	 * destKey → the route this visitor's counted arrival reported, so the submission can be
	 * credited to that same route row. Replayed verbatim: rebuilding it from the trail at submit
	 * time would name a different route whenever the visitor browsed in between.
	 */
	ap: Record<string, ArrivalRoute>;
}

export interface ArrivalRoute {
	/** Steps exactly as sent on arrival. */
	t: number[];
	/** The button the arrival was credited to, when there was one. */
	c: CtaRef | null;
	/** Arrival counted at (epoch ms). */
	at: number;
}

const LS_KEY = 'td_abt_j';

/** How long a counted metric stays counted for one visitor. */
const DEDUPE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * How recent a click has to be to name the arrival it produced. A click toward a
 * destination is followed by the arrival within seconds (a Swup navigation) or a
 * page load; half an hour is generous. Without a bound, a button clicked weeks
 * ago would be credited with a visitor who later came back from an email.
 */
const CLICK_FRESH_MS = 30 * 60 * 1000;

const FLAG_BUCKETS = ['r', 'rc', 'cc', 'a', 'fv', 'fs', 'fd', 'fl'] as const;

const empty = (): JourneyState => ({
	t: [],
	c: {},
	ct: {},
	r: {},
	rc: {},
	cc: {},
	a: {},
	fv: {},
	fs: {},
	fd: {},
	at: {},
	fl: {},
	ap: {},
});

/**
 * Bring a stored state up to date: stamp legacy `1` flags and drop expired ones.
 *
 * A legacy flag carries no date. Stamping it "now" means everyone already
 * counted before this shipped stays counted for one more window rather than
 * being counted a second time the moment the new bundle loads.
 */
const normalise = (state: JourneyState): { state: JourneyState; changed: boolean } => {
	const now = Date.now();
	let changed = false;
	for (const bucket of FLAG_BUCKETS) {
		const flags = state[bucket] ?? {};
		const kept: Record<string, number> = {};
		for (const [key, value] of Object.entries(flags)) {
			const legacy = value === 1;
			const at = legacy ? now : Number(value);
			if (Number.isFinite(at) && now - at < DEDUPE_WINDOW_MS) {
				kept[key] = at;
				if (legacy) changed = true;
			} else {
				changed = true;
			}
		}
		state[bucket] = kept;
	}
	return { state, changed };
};

const isFresh = (at: number | undefined): boolean => typeof at === 'number' && Date.now() - at < DEDUPE_WINDOW_MS;

let mem: JourneyState | null = null;

export const load = (): JourneyState => {
	if (mem) return mem;
	return reload();
};

/**
 * Read straight from storage, discarding the in-memory snapshot.
 *
 * Claims MUST go through this. A second module instance (Swup re-executing the
 * bundle) starts with its own snapshot taken at its own boot, so a slot another
 * instance has already claimed still looks free to it — which double-counts the
 * visitor. Re-reading before every claim makes localStorage the single source
 * of truth, whatever else is running on the page.
 */
const reload = (): JourneyState => {
	try {
		const raw = window.localStorage.getItem(LS_KEY);
		if (!raw) {
			mem = empty();
			return mem;
		}
		const { state, changed } = normalise({ ...empty(), ...(JSON.parse(raw) as JourneyState) });
		mem = state;
		// Persist the stamp immediately. A legacy flag stamped only in memory would be
		// re-stamped "now" on every page that happens not to save, and would never expire.
		if (changed) window.localStorage.setItem(LS_KEY, JSON.stringify(mem));
	} catch {
		mem = mem ?? empty();
	}
	return mem;
};

export const save = (): void => {
	if (!mem) return;
	try {
		window.localStorage.setItem(LS_KEY, JSON.stringify(mem));
	} catch {
		/* storage blocked — in-memory state still works for this pageview */
	}
};

/**
 * Claim a one-per-visitor slot. Returns true only the first time, so callers
 * read as `if (claim('a', destKey)) send(...)`.
 */
export const claim = (bucket: 'r' | 'a' | 'fv' | 'fs' | 'fd', key: string): boolean => {
	const state = reload();
	if (isFresh(state[bucket][key])) return false;
	state[bucket][key] = Date.now();
	save();
	return true;
};

/**
 * Claim one field metric for this visitor.
 *
 * Field rows are visitor counts, exactly like every other metric here. Before
 * this existed they were EVENT counts: focusing a field emitted `reached`, and
 * blurring it while empty emitted `reached` again, so one person tabbing
 * through a form produced "reached 7, filled 1" on a single field and a fill
 * rate that meant nothing.
 */
export const claimField = (destKey: string, guid: string, field: string, metric: string): boolean => {
	const state = reload();
	const key = `${destKey}:${guid}:${field}:${metric}`;
	if (isFresh(state.fl[key])) return false;
	state.fl[key] = Date.now();
	save();
	return true;
};

/** Same, for the per-button buckets. */
export const claimCta = (bucket: 'rc' | 'cc', destKey: string, ctaKey: string): boolean => {
	const state = reload();
	const key = `${destKey}:${ctaKey}`;
	if (isFresh(state[bucket][key])) return false;
	state[bucket][key] = Date.now();
	save();
	return true;
};

/** Append a page to the trail, capped, skipping an immediate repeat. */
export const pushStep = (postId: number, maxSteps: number): void => {
	if (postId <= 0) return;
	const state = reload();
	const last = state.t[state.t.length - 1];
	if (last && last[0] === postId) return;
	state.t.push([postId, Date.now()]);
	if (state.t.length > maxSteps) state.t = state.t.slice(-maxSteps);
	save();
};

export const steps = (): number[] => load().t.map(([id]) => id);

export const rememberCta = (destKey: string, cta: CtaRef): void => {
	const state = reload();
	state.c[destKey] = cta;
	state.ct[destKey] = Date.now();
	save();
};

/**
 * The button that produced an arrival — only if it was clicked recently. A click
 * with no timestamp predates this rule and is treated as stale, so the arrival
 * reads as direct rather than borrowing a click of unknown age.
 */
export const entryCta = (destKey: string): CtaRef | null => {
	const state = load();
	const clickedAt = state.ct?.[destKey];
	if (typeof clickedAt !== 'number' || Date.now() - clickedAt > CLICK_FRESH_MS) return null;
	return state.c[destKey] ?? null;
};

export const markArrival = (destKey: string): void => {
	const state = reload();
	state.at[destKey] = Date.now();
	save();
};

export const arrivalAt = (destKey: string): number | null => load().at[destKey] ?? null;

/** Remember the route a counted arrival reported. */
export const rememberArrivalRoute = (destKey: string, route: number[], cta: CtaRef | null): void => {
	const state = reload();
	state.ap[destKey] = { t: route, c: cta, at: Date.now() };
	save();
};

/** The route to credit a submission to — only while the arrival it came from is still counted. */
export const arrivalRoute = (destKey: string): ArrivalRoute | null => {
	const route = load().ap?.[destKey];
	if (!route || !Array.isArray(route.t) || !isFresh(route.at)) return null;
	return route;
};
